/**
 * Real authentication: Supabase Auth, the provider Phase 2 already connected (ADR-002).
 *
 * The flow is one step: a password sign-in against an account whose address the provider has
 * confirmed. PO 7.10 removed the second factor from the admin surface, and this file is where that
 * removal is real rather than merely unused — there is no challenge, no enrollment and no factor
 * lookup left in it.
 *
 * WHAT THAT MEANS FOR THE PROVIDER CALLS
 * --------------------------------------
 * `state()` reads the stored session and nothing else. It does not call `mfa.listFactors()`, which
 * is a `getUser()` round trip, and it does not call `mfa.getAuthenticatorAssuranceLevel()`. Two
 * things follow. An ordinary sign-in now touches the network exactly once, for the sign-in itself.
 * And the failure mode that produced the previous defect — an unreachable provider read as «this
 * account has no second factor», which led to the one branch that WRITES to the account — cannot
 * occur, because there is no such branch any more.
 *
 * The assurance level is still reported in `AuthState.aal`, decoded locally from the token's own
 * claim. It is information, not a gate.
 *
 * NOTHING IS REMOVED FROM ANYBODY'S ACCOUNT. No factor is unenrolled here. A TOTP factor a person
 * enrolled under the old rule stays at the provider, untouched, and would work again unchanged if
 * the decision were reversed.
 *
 * The access token is read from the provider on every request rather than cached here, so a silent
 * refresh is picked up without the app noticing. Passwords are never stored.
 */
import { type AuthError, createClient, type SupabaseClient } from '@supabase/supabase-js';
import {
  type AuthAdapter,
  type AuthState,
  type RecoveryGrant,
  type RecoverySession,
  SIGNED_OUT,
} from './adapter';

/**
 * Storage for the recovery client: this object, and nothing that outlives the page.
 *
 * `persistSession: false` alone would still let supabase-js reach for `localStorage` on some
 * paths; handing it a store of our own is what makes «the recovery session is never written down»
 * a property of the code rather than a hope. Closing the tab is therefore the end of it.
 */
class MemoryAuthStorage {
  private readonly values = new Map<string, string>();
  getItem(key: string): string | null {
    return this.values.get(key) ?? null;
  }
  setItem(key: string, value: string): void {
    this.values.set(key, value);
  }
  removeItem(key: string): void {
    this.values.delete(key);
  }
  clear(): void {
    this.values.clear();
  }
}

export class SupabaseAuthAdapter implements AuthAdapter {
  readonly name = 'supabase_auth';
  readonly supportsRecovery = true;
  private readonly client: SupabaseClient;
  private readonly url: string;
  private readonly anonKey: string;
  /** Built on the first recovery link and dropped the moment that link is done with. */
  private recovery: SupabaseClient | null = null;
  private recoveryStore: MemoryAuthStorage | null = null;

  constructor(url: string, anonKey: string) {
    this.url = url;
    this.anonKey = anonKey;
    this.client = createClient(url, anonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
        flowType: 'pkce',
        storageKey: 'hpw-credentials-auth',
      },
      global: { headers: { 'X-Client-Info': 'hpw-credentials-frontend' } },
    });
  }

  /**
   * The session, read locally, and nothing else asked of the provider.
   *
   * There is one decision left to make — is there a session or not — and supabase-js already holds
   * the answer. The assurance level is decoded from the access token's own `aal` claim by the same
   * local call, so it is reported without a round trip and without being able to fail.
   *
   * What used to be here was a second and a third provider call whose answers decided between an
   * MFA challenge and an enrollment screen. Both are gone, so the states they led to are
   * unreachable and this method cannot throw.
   */
  private async state(): Promise<AuthState> {
    const { data } = await this.client.auth.getSession();
    const session = data.session;
    if (!session) return SIGNED_OUT;
    return {
      status: 'ready',
      email: session.user?.email ?? null,
      aal: aalOf(session.access_token),
      hasToken: true,
    };
  }

  async restore(): Promise<AuthState> {
    return this.state();
  }

  async signInWithPassword(email: string, password: string): Promise<AuthState> {
    const { error } = await this.client.auth.signInWithPassword({ email, password });
    if (error) throw toAuthFailure(error);
    return this.state();
  }

  async signOut(): Promise<void> {
    // global scope: signing out here invalidates the session everywhere, which is what an
    // administrator pressing "Çıkış" on a shared machine expects
    await this.client.auth.signOut({ scope: 'global' });
  }

  async getAccessToken(): Promise<string | null> {
    const { data } = await this.client.auth.getSession();
    return data.session?.access_token ?? null;
  }

  onChange(listener: (state: AuthState) => void): () => void {
    const { data } = this.client.auth.onAuthStateChange(() => {
      /* `state()` reads the stored session and cannot reject; the catch is belt and braces so a
         future edit cannot turn a refresh into an unhandled rejection. */
      void this.state().then(listener, () => listener(SIGNED_OUT));
    });
    return () => data.subscription.unsubscribe();
  }

  /* ------------------------------------------------------------------ password recovery */

  /**
   * A client of its own, for a session that must never become a sign-in.
   *
   * It is a second `createClient` rather than a mode of the first one, and that is the whole
   * security argument: it carries its own in-memory storage, so it cannot write into
   * `hpw-credentials-auth`, cannot be picked up by `restore()` on the next load, and cannot be
   * refreshed in the background into something the console would accept. `detectSessionInUrl`
   * stays false here too — the address is read deliberately by `parseRecoveryLink`, never by the
   * client noticing it.
   */
  private recoveryClient(): SupabaseClient {
    if (!this.recovery) {
      this.recoveryStore = new MemoryAuthStorage();
      this.recovery = createClient(this.url, this.anonKey, {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
          detectSessionInUrl: false,
          flowType: 'pkce',
          storage: this.recoveryStore,
          storageKey: 'hpw-credentials-recovery',
        },
        global: { headers: { 'X-Client-Info': 'hpw-credentials-frontend' } },
      });
    }
    return this.recovery;
  }

  private dropRecovery(): void {
    this.recovery = null;
    this.recoveryStore?.clear();
    this.recoveryStore = null;
  }

  async beginPasswordRecovery(grant: RecoveryGrant): Promise<RecoverySession> {
    /* Whatever a previous attempt in this tab left behind is not what answers this link. */
    await this.endPasswordRecovery();
    const client = this.recoveryClient();

    if (grant.kind === 'session') {
      const { error } = await client.auth.setSession({
        access_token: grant.accessToken,
        refresh_token: grant.refreshToken,
      });
      if (error) throw toRecoveryFailure(error);
    } else if (grant.kind === 'token_hash') {
      const { error } = await client.auth.verifyOtp({
        token_hash: grant.tokenHash,
        type: 'recovery',
      });
      if (error) throw toRecoveryFailure(error);
    } else {
      /*
       * PKCE. Supabase's rule is that the code verifier is written when the flow is STARTED and
       * the exchange must happen in that same browser. Nothing in this application starts one —
       * there is no self-service reset here — so this store holds no verifier and the exchange
       * fails. That is the correct outcome rather than a gap: the link is refused with the same
       * sentence as any other unusable link, instead of half-opening a form.
       */
      const { error } = await client.auth.exchangeCodeForSession(grant.code);
      if (error) throw toRecoveryFailure(error);
    }

    /*
     * The provider is asked who this is, rather than the answer being read out of the token.
     *
     * `setSession` will accept a well-formed JWT without going to the server, so a link whose
     * token was revoked, tampered with or simply belongs to nothing would otherwise open the form.
     * `getUser` is a round trip and it is the one that decides.
     */
    const { data, error } = await client.auth.getUser();
    if (error || !data.user) {
      await this.endPasswordRecovery();
      throw toRecoveryFailure(error);
    }
    return { email: data.user.email ?? null };
  }

  /**
   * Asks the provider to mail a recovery link, and tells the caller nothing about the address.
   *
   * Supabase answers this endpoint the same way whether or not an account exists, which is the
   * behaviour the screen depends on. What is mapped here is therefore only about the REQUEST: a
   * refusal to take it at all. Everything other than a rate limit becomes one generic code, so
   * there is no branch in which «no such user» could acquire a sentence of its own, and no provider
   * text reaches the screen.
   *
   * The address the link comes back to is built from THIS application's own origin plus the route
   * that completes it. Nothing about it is derived from what somebody typed, so there is no input
   * that could redirect the mail somewhere else.
   */
  async requestPasswordRecovery(email: string): Promise<void> {
    const { error } = await this.client.auth.resetPasswordForEmail(email, {
      redirectTo: recoveryRedirectUrl(),
    });
    if (error) throw toRecoveryRequestFailure(error);
  }

  async completePasswordRecovery(newPassword: string): Promise<void> {
    const client = this.recovery;
    if (!client) throw new AuthFailure('recovery_link_invalid');
    const { data } = await client.auth.getSession();
    if (!data.session) throw new AuthFailure('recovery_link_invalid');

    const { error } = await client.auth.updateUser({ password: newPassword });
    if (error) throw toRecoveryFailure(error);

    /*
     * The password has changed, so every session built on the old one is stale — the sessions this
     * link's own tokens could still open among them. A global sign-out is what makes a spent link
     * useless, rather than merely scrubbed out of the address bar.
     */
    await client.auth.signOut({ scope: 'global' }).catch(() => undefined);
    this.dropRecovery();
  }

  async endPasswordRecovery(): Promise<void> {
    const client = this.recovery;
    if (!client) return;
    this.dropRecovery();
    /*
     * Local. An abandoned recovery must not sign the person out of a console session they left
     * open in another tab; a completed one was already retired globally, above.
     */
    await client.auth.signOut({ scope: 'local' }).catch(() => undefined);
  }
}

/**
 * The assurance level, read from the access token's own claim.
 *
 * Local and total: an unreadable token reports `null` rather than throwing, because this value is
 * carried for information and nothing branches on it. The signature is NOT checked here and must
 * not be relied on for anything — the server verifies the token with the provider, and this is a
 * label on a screen.
 */
export function aalOf(accessToken: string | null | undefined): 'aal1' | 'aal2' | null {
  const payload = accessToken?.split('.')[1];
  if (!payload) return null;
  try {
    const json = JSON.parse(
      decodeURIComponent(
        atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
          .join(''),
      ),
    ) as { aal?: unknown };
    return json.aal === 'aal2' ? 'aal2' : json.aal === 'aal1' ? 'aal1' : null;
  } catch {
    return null;
  }
}

/** Provider failures are mapped to stable codes; the provider's own text is never shown raw. */
export class AuthFailure extends Error {
  readonly code: string;
  constructor(code: string) {
    super(code);
    this.name = 'AuthFailure';
    this.code = code;
  }
}

function toAuthFailure(error: AuthError | null | undefined): AuthFailure {
  const raw = `${error?.code ?? ''} ${error?.message ?? ''}`.toLowerCase();
  if (raw.includes('invalid login') || raw.includes('invalid_credentials')) {
    return new AuthFailure('invalid_credentials');
  }
  if (raw.includes('email not confirmed')) return new AuthFailure('email_not_confirmed');
  if (raw.includes('rate limit') || raw.includes('over_request_rate')) return new AuthFailure('rate_limited');
  return new AuthFailure('auth_failed');
}

/**
 * Recovery failures, mapped fail-closed.
 *
 * Only two outcomes leave the password form usable, and they are the two that are about the
 * password rather than the link: the provider found it too weak, or it is the one already in use.
 * Everything else — an expired link, a spent link, a session the provider will not verify, a
 * refusal we do not recognise — becomes `recovery_link_invalid`, which is what closes the form.
 * A default that meant «try again» would keep a password form open on a link the provider has
 * already rejected, which is exactly the state this flow must not have.
 */
function toRecoveryFailure(error: AuthError | null | undefined): AuthFailure {
  const raw = `${error?.code ?? ''} ${error?.message ?? ''}`.toLowerCase();
  if (raw.includes('weak_password') || raw.includes('password should be at least')) {
    return new AuthFailure('recovery_password_weak');
  }
  if (raw.includes('same_password') || raw.includes('should be different from the old password')) {
    return new AuthFailure('recovery_password_same');
  }
  if (raw.includes('rate limit') || raw.includes('over_request_rate') || raw.includes('over_email_send')) {
    return new AuthFailure('rate_limited');
  }
  return new AuthFailure('recovery_link_invalid');
}

/**
 * The route that completes a recovery link — the one `ProductSurface` serves at `reset-password`.
 *
 * Not exported: the test that matters asserts the literal address the mail will carry, which is a
 * stronger check than comparing against this constant. Nothing else needs it.
 */
const RECOVERY_REDIRECT_PATH = '/admin/reset-password';

/**
 * Where a recovery mail comes back to: this application's own origin and nothing else.
 *
 * Read from the browser rather than from configuration on purpose. `publicBaseUrl` is the base for
 * PUBLIC credential pages and is set independently, so a deployment could point it elsewhere and
 * silently send recovery mail to an origin that does not serve this console. The origin the person
 * is already on is the one that can complete the link, and it is not something a request can set.
 */
function recoveryRedirectUrl(): string {
  return `${window.location.origin}${RECOVERY_REDIRECT_PATH}`;
}

/**
 * Why a recovery REQUEST could not be made — never why it might not apply.
 *
 * Two outcomes only. A rate limit is worth saying, because waiting is the thing to do about it.
 * Everything else is one code: an unrecognised refusal, a provider that is down, a body it would
 * not accept. Nothing distinguishes an address with an account from one without, because the only
 * branch here is on the provider's own rate-limit signal.
 */
function toRecoveryRequestFailure(error: AuthError | null | undefined): AuthFailure {
  const raw = `${error?.code ?? ''} ${error?.message ?? ''}`.toLowerCase();
  if (raw.includes('rate limit') || raw.includes('over_request_rate') || raw.includes('over_email_send')) {
    return new AuthFailure('rate_limited');
  }
  return new AuthFailure('recovery_request_failed');
}

/**
 * The failures that are about the password and not about the link.
 *
 * The screen asks this rather than listing codes of its own, so «which refusals keep the form
 * open» is decided once, next to the mapping that produces them.
 */
export function keepsRecoveryFormOpen(error: unknown): boolean {
  if (!(error instanceof AuthFailure)) return false;
  return (
    error.code === 'recovery_password_weak' ||
    error.code === 'recovery_password_same' ||
    error.code === 'rate_limited'
  );
}

/** Turkish, user-safe text per auth failure code. */
export const AUTH_MESSAGES: Record<string, string> = {
  invalid_credentials: 'E-posta ya da parola hatalı.',
  email_not_confirmed: 'E-posta adresiniz doğrulanmamış. Gelen kutunuzu kontrol edin.',
  rate_limited: 'Çok fazla deneme yapıldı. Kısa bir süre sonra yeniden deneyin.',
  auth_failed: 'Giriş yapılamadı. Bilgilerinizi kontrol edip yeniden deneyin.',
  provider_not_configured: 'Kimlik sağlayıcısı yapılandırılmamış. Yöneticinize başvurun.',
  /*
   * One sentence for every unusable link, whatever made it unusable. An expired link, a link that
   * has already been used and a link whose session the provider will not verify are told apart by
   * nothing the reader can see — which is deliberate: the difference is only useful to someone
   * probing the address.
   */
  recovery_link_invalid:
    'Bu parola yenileme bağlantısı geçersiz veya süresi dolmuş. Yeni bir bağlantı isteyin.',
  recovery_password_weak:
    'Bu parola sağlayıcı tarafından yeterince güçlü bulunmadı. Daha uzun bir parola seçin.',
  recovery_password_same: 'Yeni parolanız eskisiyle aynı olamaz. Farklı bir parola seçin.',
  recovery_not_supported: 'Bu ortamda parola yenilenemez. Yöneticinize başvurun.',
  /*
   * Said when the REQUEST did not go through, and never about the address.
   *
   * Claiming a mail had been sent would be a lie when the provider refused to take the request; the
   * sentence therefore reports the attempt, not the account, and the same text covers every reason.
   */
  recovery_request_failed: 'Parola yenileme bağlantısı istenemedi. Kısa bir süre sonra yeniden deneyin.',
};

/** Shown when the provider gave no code we recognise; never echoes the provider's own text. */
const AUTH_FALLBACK = 'Giriş yapılamadı. Bilgilerinizi kontrol edip yeniden deneyin.';

export function authMessage(error: unknown): string {
  if (error instanceof AuthFailure) return AUTH_MESSAGES[error.code] ?? AUTH_FALLBACK;
  return AUTH_FALLBACK;
}

/**
 * The same thing for the recovery screen, with the fail-closed sentence as its fallback.
 *
 * `authMessage` falls back to «Giriş yapılamadı», which is both wrong and misleading on a screen
 * that is not a sign-in. Anything unrecognised here is treated as an unusable link, which is the
 * safe reading and the one that closes the form.
 */
export function recoveryMessage(error: unknown): string {
  if (error instanceof AuthFailure) {
    return AUTH_MESSAGES[error.code] ?? AUTH_MESSAGES.recovery_link_invalid ?? AUTH_FALLBACK;
  }
  return AUTH_MESSAGES.recovery_link_invalid ?? AUTH_FALLBACK;
}
